/**
 * @copyright
 * Copyright (c) 2024 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import ITheme from "@atg/icf-react-components/themes/Theme";
import { FC, memo, useEffect, useState } from "react";
import { createUseStyles } from "react-jss";
import { useSelector } from "react-redux";

import bgHeaderImg from "~/assets/images/header_bg.jpeg";
import {
  JSS_FONT_FAMILY_PRIMARY,
  JSS_FONT_FAMILY_SECONDARY,
  JSS_MEDIA_TABLET_MAX,
  PAGE_CONTENT_MAX_WIDTH,
  PAGE_CONTENT_PADDING,
} from "~/constants";
import { FIXED_HEADER_HEIGHT, PAGE_HEADER_HEIGHT } from "~/features/header/constants";
import { selectMenuWidth } from "~/features/header/selectors";
import useScrollStatus from "~/hooks/useScrollStatus";
import customColors from "~/utils/customColors";

type TitleLength = "short" | "medium" | "long";

interface StyleProps {
  desktopMenuWidth: number;
  titleLength: TitleLength;
  titleHasNumbers: boolean;
}

const useStyles = createUseStyles<string, StyleProps, ITheme>((theme) => ({
  header: {
    background: customColors.lightBlack,
  },
  image: {
    height: `${PAGE_HEADER_HEIGHT}px`,
    transition: "opacity 0.15s ease",
    backgroundImage: `linear-gradient(0deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.6) 100%), url(${bgHeaderImg})`,
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "auto 100%",
    display: "flex",
    alignItems: "flex-end",
    [JSS_MEDIA_TABLET_MAX]: {
      backgroundImage: "none",
      height: `${FIXED_HEADER_HEIGHT}px`,
    },
  },
  container: {
    width: PAGE_CONTENT_MAX_WIDTH,
    maxWidth: "100%",
    padding: `0 ${PAGE_CONTENT_PADDING}px`,
    boxSizing: "border-box",
    margin: "0 auto",
    display: "flex",
    justifyContent: "flex-end",
    "& $title": {
      color: theme.colors.baseContrast100,
      fontSize: (props) => {
        switch (props.titleLength) {
          case "short":
            return "54px";
          case "medium":
            return "42px";
          default:
            return "36px";
        }
      },
      fontWeight: 300,
      lineHeight: 1.1,
      letterSpacing: (props) => (props.titleLength === "short" ? "6.9px" : "5.5px"),
      marginBottom: 0,
      fontFamily: (props) => (props.titleHasNumbers ? JSS_FONT_FAMILY_PRIMARY : JSS_FONT_FAMILY_SECONDARY),
    },
  },
  title: {
    textTransform: "uppercase",
    width: (props) => `${props.desktopMenuWidth}px`,
    height: "138px",
    display: "flex",
    alignItems: "center",
    "& span": {
      // eslint-disable-next-line @atg/css-rtl-support
      marginLeft: "-2px",
      display: "block",
    },
    [JSS_MEDIA_TABLET_MAX]: {
      display: "none",
    },
  },
  skeleton: {
    height: "138px",
  },
}));

const getTitleLength = (title: string, wrapperWidth: number): TitleLength => {
  if (title.length / wrapperWidth < 0.025) {
    return "short";
  }

  if (title.length / wrapperWidth < 0.033) {
    return "medium";
  }

  return "long";
};

interface Props {
  title: string;
}

const PageHeader: FC<Props> = ({ title }) => {
  const menuWidth = useSelector(selectMenuWidth);
  const { scrollHeaderPercent } = useScrollStatus();

  const [width, setWidth] = useState(0);

  const titleLength: TitleLength = getTitleLength(title, width);
  const titleHasNumbers = !!title.match(/\d+/g);

  useEffect(() => {
    setWidth(menuWidth);
  }, [menuWidth]);

  const classes = useStyles({ desktopMenuWidth: width, titleLength, titleHasNumbers });

  return (
    <header className={classes.header}>
      <div className={classes.image} style={{ opacity: 1 - scrollHeaderPercent }}>
        <div className={classes.container}>
          <h1 className={classes.title}>
            <span>{title}</span>
          </h1>
        </div>
      </div>
    </header>
  );
};

export default memo(PageHeader);
